import { useState } from "react";
import { EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { tv } from "tailwind-variants";
import z from "zod";

import { CreateAdminModal } from "@/components/admins/create-admin-modal";
import { DeleteAdminModal } from "@/components/admins/delete-admin-modal";
import { useAdmin } from "@/hooks/use-admin";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { AdminListItem } from "@/types/admin";
import {
  Badge,
  BADGE_STATUS,
  Button,
  ScreenContent,
  ScreenHeader,
  ScreenTitle,
  Tooltip,
} from "@/ui";
import { PaginatedTable } from "@/ui/common/paginated-table";

const rowAction = tv({
  base: [
    "inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-content-muted",
    "transition-colors duration-150 hover:bg-surface-hover hover:text-content",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  variants: {
    destructive: {
      true: "text-danger-content hover:bg-danger-surface hover:text-danger-content",
    },
  },
});

const AdminsPage = () => {
  const [createAdminModalOpen, setCreateAdminModalOpen] = useState(false);
  const [deleteAdminModalOpen, setDeleteAdminModalOpen] = useState(false);
  const [deletedAdmin, setDeletedAdmin] = useState<AdminListItem | null>(null);

  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: [],
  });

  const { getAdmins, resendAdminPasswordLink } = useAdmin();

  const { data: admins, isLoading: isLoadingAdmins } = getAdmins({
    queryBuilder,
  });

  const { isPending: isResendingLink, mutate: resendPasswordLink } = resendAdminPasswordLink();

  const columns: ColumnDef<AdminListItem>[] = [
    { id: "name", header: "name", accessorKey: "name" },
    { id: "email", header: "email", accessorKey: "email" },
    {
      id: "role",
      header: "role",
      accessorKey: "role",
      cell: ({ row }) => {
        return <span className="capitalize">{row.original.role}</span>;
      },
    },
    {
      id: "password",
      header: "password",
      cell: ({ row }) => {
        const { hasValidPassword, passwordExpiresAt } = row.original;

        if (hasValidPassword) {
          return <Badge status={BADGE_STATUS.SUCCESS}>Active</Badge>;
        }

        const passwordWasNeverSet = !passwordExpiresAt;

        return (
          <Badge status={passwordWasNeverSet ? BADGE_STATUS.DEFAULT : BADGE_STATUS.WARNING}>
            {passwordWasNeverSet ? "Not set" : "Expired"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-1">
            {!row.original.hasValidPassword ? (
              <Tooltip content="Re-send set-password link">
                <button
                  aria-label={`Re-send set-password link to ${row.original.name}`}
                  className={rowAction()}
                  disabled={isResendingLink}
                  onClick={(event) => {
                    event.stopPropagation();
                    resendPasswordLink(row.original.id);
                  }}
                  type="button"
                >
                  <EnvelopeIcon aria-hidden="true" className="size-5" />
                </button>
              </Tooltip>
            ) : null}

            <Tooltip content="Delete Admin">
              <button
                aria-label={`Delete ${row.original.name}`}
                className={rowAction({ destructive: true })}
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteAdminModalOpen(true);
                  setDeletedAdmin(row.original);
                }}
                type="button"
              >
                <TrashIcon aria-hidden="true" className="size-5" />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ScreenHeader>
        <ScreenTitle>Admins</ScreenTitle>

        <Button
          onClick={() => {
            setCreateAdminModalOpen(true);
          }}
          variant="secondary"
        >
          Create Admin
        </Button>
      </ScreenHeader>

      <ScreenContent>
        <PaginatedTable
          columns={columns}
          data={admins?.data}
          isLoadingData={isLoadingAdmins}
          pagination={admins?.pagination}
          rowLink={(admin) => {
            return { to: "/admins/$id", params: { id: admin.id.toString() } };
          }}
          setCurrentPage={setCurrentPage}
          setSorting={setSorting}
          sorting={sortingState}
        />
      </ScreenContent>

      <CreateAdminModal
        isOpen={createAdminModalOpen}
        onClose={() => {
          setCreateAdminModalOpen(false);
        }}
      />

      {deletedAdmin ? (
        <DeleteAdminModal
          admin={deletedAdmin}
          isOpen={deleteAdminModalOpen}
          onClose={() => {
            setDeleteAdminModalOpen(false);
            setDeletedAdmin(null);
          }}
        />
      ) : null}
    </>
  );
};

export const Route = createFileRoute("/_private/admins/")({
  component: AdminsPage,
  validateSearch: z.object({
    page: z.number().optional(),
    orderBy: z.string().optional(),
    sortDirection: z.string().optional(),
  }),
});
